import { Upload, message } from "antd";
import { LoadingOutlined, PlusOutlined } from "@ant-design/icons";
import { useState } from "react";
import { TOKEN_KEY } from "../authProvider";

type UploadType =
  | "ACTIVITY"
  | "PROFILE"
  | "BUSINESS_PRODUCT"
  | "BUSINESS_OFFER"
  | "WORKOUT_PREFERENCE"
  | "TOURNAMENT"
  | "PROFILE_COVER"
  | "PROFILE_PROMO"
  | "COACH_CONTRACT"
  | "SIGNED_COACH_CONTRACT"
  | "MISSION"
  | "ACTIVITY_SESSION"
  | "CATEGORY"
  | "BANNER"
  | "SCOUTING_EVENT"
  | "CARD_TEMPLATE"
  | "AD_CREATIVE";

type MimeType = "IMAGE" | "VIDEO" | "PDF";

// Derive mimeType from file's content type
function deriveMimeType(contentType: string): MimeType {
  if (contentType.startsWith("video/")) return "VIDEO";
  if (contentType.startsWith("image/")) return "IMAGE";
  if (contentType === "application/pdf") return "PDF";
  return "IMAGE"; // Default fallback
}

interface ImageUploadProps {
  uploadType?: UploadType;
  value?: string;
  onChange?: (fileId: string) => void;
  accept?: string; // e.g., "image/*,video/*" to accept both
}

export const ImageUpload: React.FC<ImageUploadProps> = ({
  uploadType = "BUSINESS_OFFER",
  value,
  onChange,
  accept = "image/*,video/*", // Default to accepting both images and videos
}) => {
  const [loading, setLoading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string>();
  const [mediaType, setMediaType] = useState<MimeType>("IMAGE");

  const handleUpload = async (file: File) => {
    try {
      setLoading(true);

      const detectedMimeType = deriveMimeType(file.type);
      const token = localStorage.getItem(TOKEN_KEY);
      const presignRes = await fetch(
        `${import.meta.env.VITE_API_URL}/file/presigned_url`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            fileCount: 1,
            mimeType: detectedMimeType,
            contentType: file.type,
            uploadType,
          }),
        }
      );

      const presignJson = await presignRes.json();
      const presigned = presignJson?.data?.[0];
      if (!presigned?.url || !presigned?.fields || !presigned?.fileID) {
        throw new Error("Failed to get upload URL");
      }

      const {
        url: uploadUrl,
        fields: uploadFields,
        fileID: uploadFileID,
      } = presigned;

      const formData = new FormData();
      Object.entries(uploadFields).forEach(([key, value]) => {
        formData.append(key, value as string);
      });
      formData.append("Content-Type", file.type);
      formData.append("file", file);

      const uploadResponse = await fetch(uploadUrl, {
        method: "POST",
        body: formData,
      });

      if (!uploadResponse.ok) {
        throw new Error("Failed to upload file");
      }

      onChange?.(uploadFileID);
      message.success("File uploaded successfully");
      setPreviewUrl(URL.createObjectURL(file));
      setMediaType(detectedMimeType);
    } catch (error) {
      message.error("Upload failed");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const renderPreview = () => {
    if (!previewUrl) {
      return (
        <div>
          {loading ? <LoadingOutlined /> : <PlusOutlined />}
          <div style={{ marginTop: 8 }}>Upload</div>
        </div>
      );
    }

    if (mediaType === "VIDEO") {
      return (
        <video
          src={previewUrl}
          style={{ width: '100%', maxHeight: '100%', objectFit: 'cover' }}
          controls
        />
      );
    }

    return <img src={previewUrl} alt="preview" style={{ width: '100%' }} />;
  };

  return (
    <Upload
      name="file"
      listType="picture-card"
      showUploadList={false}
      accept={accept}
      beforeUpload={(file) => {
        handleUpload(file);
        return false; // Prevent default upload behavior
      }}
    >
      {renderPreview()}
    </Upload>
  );
};
